import React, { useEffect, useMemo, useState } from 'react';
import { competitionsWithRows } from '../../utils/entityHubRenderable';

function isUnusedStat(value) {
  return value == null || value === '' || value === '—';
}

/** Football P/W/D/L unused → race/championship style (wins + points). */
function isWinsPointsTable(table) {
  const name = String(table?.competitionName || '').toLowerCase();
  if (/\bdrivers?\b|\bconstructors?\b|\bchampionship\b|\bformula\b|\bf1\b/.test(name)) {
    return true;
  }
  const rows = table?.rows;
  if (!Array.isArray(rows) || !rows.length) return false;
  return rows.every(
    (row) =>
      isUnusedStat(row.played) &&
      isUnusedStat(row.drawn) &&
      isUnusedStat(row.lost)
  );
}

function nameColumnLabel(table) {
  const name = String(table?.competitionName || '').toLowerCase();
  if (/\bdrivers?\b/.test(name)) return 'Driver';
  if (/\bconstructors?\b/.test(name)) return 'Team';
  return 'Team';
}

function ColLabel({ full, short }) {
  return (
    <>
      <span className="entity-hub-th-full">{full}</span>
      <span className="entity-hub-th-short">{short}</span>
    </>
  );
}

function StandingsPanel({ hub, entityId }) {
  const tables = useMemo(() => competitionsWithRows(hub), [hub]);
  const firstId = tables[0]?.competitionId || tables[0]?.competitionName || '0';
  const [activeId, setActiveId] = useState(firstId);

  useEffect(() => {
    setActiveId(firstId);
  }, [firstId]);

  const activeTable = useMemo(() => {
    const found = tables.find(
      (t) => (t.competitionId || t.competitionName) === activeId
    );
    return found || tables[0];
  }, [tables, activeId]);

  if (!tables.length || !activeTable) return null;

  const winsPoints = isWinsPointsTable(activeTable);
  const subjectLabel = nameColumnLabel(activeTable);

  return (
    <div className="entity-hub-panel entity-hub-standings">
      <h3 className="entity-hub-standings-title">Standings</h3>
      {tables.length > 1 && (
        <div className="entity-hub-comp-tabs" role="tablist" aria-label="Competitions">
          {tables.map((t) => {
            const id = t.competitionId || t.competitionName;
            const selected = (activeTable.competitionId || activeTable.competitionName) === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`entity-hub-comp-tab${selected ? ' is-active' : ''}`}
                onClick={() => setActiveId(id)}
              >
                {t.competitionName || 'Competition'}
              </button>
            );
          })}
        </div>
      )}
      <div className="entity-hub-table-wrap">
        <table
          className={`entity-hub-table${winsPoints ? ' entity-hub-table--wins-points' : ''}`}
        >
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">{subjectLabel}</th>
              {winsPoints ? (
                <>
                  <th scope="col">
                    <ColLabel full="Wins" short="W" />
                  </th>
                  <th scope="col">
                    <ColLabel full="Points" short="Pts" />
                  </th>
                </>
              ) : (
                <>
                  <th scope="col">P</th>
                  <th scope="col">W</th>
                  <th scope="col">D</th>
                  <th scope="col">L</th>
                  <th scope="col">Pts</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {activeTable.rows.map((row, index) => {
              const teamId = row.team?.entityId;
              const highlighted = entityId && teamId && teamId === entityId;
              const key = `${teamId || row.team?.name || 'row'}-${index}`;
              return (
                <tr key={key} className={highlighted ? 'is-highlighted' : undefined}>
                  <td>{row.rank ?? '—'}</td>
                  <td>
                    <span className="entity-hub-team-cell">
                      {row.team?.crestUrl ? (
                        <img
                          className="entity-hub-team-crest"
                          src={row.team.crestUrl}
                          alt=""
                        />
                      ) : null}
                      {row.team?.name || '—'}
                    </span>
                  </td>
                  {winsPoints ? (
                    <>
                      <td>{row.won ?? '—'}</td>
                      <td>{row.points ?? '—'}</td>
                    </>
                  ) : (
                    <>
                      <td>{row.played ?? '—'}</td>
                      <td>{row.won ?? '—'}</td>
                      <td>{row.drawn ?? '—'}</td>
                      <td>{row.lost ?? '—'}</td>
                      <td>{row.points ?? '—'}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default StandingsPanel;
